import React from 'react';
import ClassroomModal from './ClassroomModal';
import type { LevelBriefing } from '../../classroom/missionLayers';
import { printWorksheet } from '../../classroom/printWorksheet';
import type { LevelLite } from '../../../curriculum/adapters/types';
import { LessonOutcomesDisplay } from './ClassesView';
import WorksheetDownloads from './WorksheetDownloads';

interface BriefingModalProps {
  isOpen: boolean;
  levelTitle: string;
  briefing: LevelBriefing;
  level?: LevelLite;
  /** When true, AI-approve slot would show — leave false until pipeline is live */
  aiApproveAvailable?: boolean;
  busy?: boolean;
  onClose: () => void;
  onPrint?: () => void;
  onStart: () => void | Promise<void>;
}

/**
 * Zero-prep Start Class briefing.
 * Slots that aren't ready (video, AI-approve) render nothing — no "coming soon" stubs.
 */
const BriefingModal: React.FC<BriefingModalProps> = ({
  isOpen,
  levelTitle,
  briefing,
  level,
  aiApproveAvailable = false,
  busy,
  onClose,
  onStart,
  onPrint,
}) => {
  if (!isOpen) return null;

  return (
    <ClassroomModal isOpen={isOpen} title="Before you start" onClose={onClose} width={level ? 900 : 520}>
      <div className="cd-briefing">
        <div className="cd-briefing-topbar">
          <p className="cd-briefing-level">{levelTitle}</p>
          <button
            type="button"
            className="cd-btn cd-class-action cd-class-action-start"
            onClick={onStart}
            disabled={busy}
          >
            {busy ? 'Starting…' : 'Start lesson'}
          </button>
        </div>

        {level && (level.thinkingSkill || level.refrain || level.iCanStatement || level.studentArtifactExample || level.aiGeneratedExample) ? <section className="cd-briefing-slot cd-briefing-outcomes"><div className="cd-briefing-slot-label">Lesson outcomes</div><div className="cd-frame-details"><LessonOutcomesDisplay level={level} /></div></section> : null}

        {/* 1. Today in one sentence */}
        <section className="cd-briefing-slot">
          <div className="cd-briefing-slot-label">Today</div>
          <p className="cd-briefing-summary">{briefing.summary}</p>
        </section>

        {/* 2. Video — absent until filmed */}
        {briefing.videoUrl ? (
          <section className="cd-briefing-slot">
            <div className="cd-briefing-slot-label">One-minute brief</div>
            <video
              className="cd-briefing-video"
              src={briefing.videoUrl}
              controls
              playsInline
              preload="metadata"
            />
          </section>
        ) : null}

        {/* 3. Worksheet — only when today has one */}
        {briefing.uploadedWorksheets.length || briefing.worksheet ? (
          <section className="cd-briefing-slot">
            <div className="cd-briefing-slot-label">Worksheets</div>
            <WorksheetDownloads onPrint={onPrint} worksheets={briefing.uploadedWorksheets} />
            {briefing.worksheet ? <><p className="cd-briefing-worksheet-copy">
              Today also has a printable worksheet: {briefing.worksheet.title}.
            </p><button
              type="button"
              className="cd-btn cd-btn-compact"
              onClick={() => printWorksheet(briefing.worksheet!)}
            >
              View / Print
            </button></> : null}
          </section>
        ) : null}

        {/* 4. AI-approve — absent until pipeline is live */}
        {aiApproveAvailable ? (
          <section className="cd-briefing-slot">
            <div className="cd-briefing-slot-label">Approve last week</div>
            <p className="cd-briefing-worksheet-copy">Review waiting AI creations before reveal.</p>
          </section>
        ) : null}

        {/* 5. Opening say-line */}
        {briefing.openingSay ? (
          <section className="cd-briefing-slot">
            <div className="cd-briefing-slot-label">First thing you’ll say</div>
            <blockquote className="cd-briefing-say">“{briefing.openingSay}”</blockquote>
          </section>
        ) : null}

      </div>
    </ClassroomModal>
  );
};

export default BriefingModal;
